import { useState, useEffect } from 'react';
import emailjs from 'emailjs-com';

const RECAPTCHA_SITE_KEY = '6LfHUj8rAAAAANSxaane7wlxlcpnA_Mw_QAjpUCx';

const QuickQuoteForm = ({ serviceTitle, city, zip, page }) => {
  const formatCityZip = (citySlug, zipCode) => {
    if (!citySlug || !zipCode) return '';
    const formattedCity = citySlug
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `${formattedCity}, CA ${zipCode}`;
  };

  const [form, setForm] = useState({
    name: '',
    phone: '',
    projectType: serviceTitle || '',
    cityZip: formatCityZip(city, zip),
    website: '',
  });

  const [errors, setErrors] = useState({});
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Wait for global reCAPTCHA script from _app.js
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        setRecaptchaReady(true);
        return true;
      }
      return false;
    };

    if (checkRecaptcha()) return;

    const intervalId = setInterval(() => {
      if (checkRecaptcha()) {
        clearInterval(intervalId);
      }
    }, 500);

    return () => clearInterval(intervalId);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Phone: only digits, max 10
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setForm((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    let valid = true;

    const trimmedName = form.name.trim();

    // At least 2 letters in the full name
    if (!/^[A-Za-z\s]+$/.test(trimmedName) || trimmedName.replace(/\s/g, '').length < 2) {
      valid = false;
      newErrors.name = 'Please enter your full name with at least 2 letters.';
    }

    // Phone: 10 digits
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(form.phone)) {
      valid = false;
      newErrors.phone = 'Please enter a 10 digit phone number (numbers only).';
    }

    // City + ZIP not empty
    if (!form.cityZip.trim()) {
      valid = false;
      newErrors.cityZip = 'Please confirm your city and ZIP.';
    }

    // Project type required
    if (!form.projectType.trim()) {
      valid = false;
      newErrors.projectType = 'Please enter your project type.';
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSuccessMessage('');
    setErrors({});

    if (form.website) {
      console.warn('Bot detected');
      return;
    }

    if (!validateForm()) return;

    if (!recaptchaReady || !window.grecaptcha) {
      alert('Security check is not ready yet, please try again in a moment.');
      return;
    }

    try {
      // reCAPTCHA v3
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });

      const recaptchaResponse = await fetch('/api/verify-recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const recaptchaResult = await recaptchaResponse.json();
      console.log('QuickQuote reCAPTCHA result', recaptchaResponse.status, recaptchaResult);

      if (!recaptchaResponse.ok) {
        alert('reCAPTCHA verification failed, please try again.');
        return;
      }

      // Split name into first and last for the template
      const trimmedName = form.name.trim();
      const [firstNameRaw, ...restName] = trimmedName.split(/\s+/);
      const firstName = firstNameRaw || '';
      const lastName = restName.join(' ') || '';

      // Try to separate city from ZIP, example "Studio City, CA 91604"
      let cityOnly = '';
      let stateOnly = 'CA';
      let messageCityZip = form.cityZip;

      const cityZipMatch = form.cityZip.match(/^(.+?),\s*([A-Z]{2})\s+(\d{5})$/);
      if (cityZipMatch) {
        cityOnly = cityZipMatch[1];
        stateOnly = cityZipMatch[2];
      }

      const serviceValue = (form.projectType || serviceTitle || '').toLowerCase() || 'quick quote';

      // Submission shaped like the main contact form template expects
      const submissionData = {
        name: trimmedName,
        firstName: firstName.toLowerCase(),
        lastName: lastName.toLowerCase(),
        email: 'unknown', // not collected in QuickQuote, leave empty or change template to hide
        phone: form.phone,
        city: cityOnly.toLowerCase(),
        state: stateOnly.toLowerCase(),
        service: serviceValue,
        financing: 'unknown',
        startTime: 'unknown',
        message: `This is a Quick Quote request for ${form.projectType || serviceTitle || ''} in ${messageCityZip} please call client`,
        'g-recaptcha-response': token,
        source: 'quick_form_zip_page',
      };

      // Send via EmailJS using same service and template
      await emailjs.send(
        'service_modernBuild',   // service ID
        'template_z9lbcbo',      // template ID
        submissionData,
        '5lbOJijq8X39pr80P'      // public key
      );

      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'form_submission', {
          event_category: 'lead',
          event_label: serviceValue || 'quick_quote',
          value: 1,
        });
      }

      setSuccessMessage(
        'Thank you, your request has been sent. A remodeling specialist will call you shortly.'
      );

      // Reset form but keep project type and cityZip prefilled
      setForm({
        name: '',
        phone: '',
        projectType: serviceTitle || '',
        cityZip: formatCityZip(city, zip),
      });
    } catch (err) {
      console.error(err);
      alert('Failed to send the message, please try again.');
    }
  };

  return (
    <section className="relative z-20">
      <div className="max-w-5xl mx-auto px-4">
        <div
          className={
            `bg-white shadow-xl rounded-2xl p-6 md:p-8 border border-[#f1eeeb] ` +
            (page === 'home'
              ? '-mt-32 md:-mt-44'
              : '-mt-10 md:-mt-14')
          }
        >
          {successMessage && (
            <div className="mb-3 text-green-600 text-sm md:text-base text-center md:text-left">
              {successMessage}
            </div>
          )}

          <h2 className="text-xl md:text-2xl font-gilda text-black mb-2 text-center md:text-left">
            Get A Fast & Free Estimate
          </h2>
          <p className="text-sm md:text-base text-gray mb-6 text-center md:text-left">
            Share a few details and a remodeling specialist will call you back to discuss your project.
          </p>

          <form autoComplete="on" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* BotProtectorInput */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={handleChange}
              style={{ display: 'none' }}
              autoComplete="off"
              tabIndex="-1"
            />
          {/* EndBotProtectorInput */}

          {/* Name */}
          <div className="flex flex-col text-left">
            <label
              htmlFor="qq-name"
              className="text-xs uppercase tracking-[2px] text-gray mb-1"
            >
              Name
            </label>
            <input
              id="qq-name"
              type="text"
              name="name"
              required
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
              autoCapitalize="words"
              className={`border rounded-md px-3 py-2 text-sm focus:outline-none ${
                errors.name ? 'border-red-500' : 'border-[#e4dfd8] focus:border-primary'
              }`}
              placeholder="* Your full name"
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Phone */}
          <div className="flex flex-col text-left">
            <label
              htmlFor="qq-phone"
              className="text-xs uppercase tracking-[2px] text-gray mb-1"
            >
              Phone
            </label>
            <input
              id="qq-phone"
              type="tel"
              name="phone"
              required
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
              className={`border rounded-md px-3 py-2 text-sm focus:outline-none ${
                errors.phone ? 'border-red-500' : 'border-[#e4dfd8] focus:border-primary'
              }`}
              placeholder="* Best number to reach you"
              pattern="\d{10}"
              maxLength={10}
              inputMode="numeric"
            />
            {errors.phone && (
              <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
            )}
          </div>

          {/* Project Type */}
          <div className="flex flex-col text-left">
            <label
              htmlFor="qq-projectType"
              className="text-xs uppercase tracking-[2px] text-gray mb-1"
            >
              Project Type
            </label>
            <input
              id="qq-projectType"
              type="text"
              name="projectType"
              required
              value={form.projectType}
              onChange={handleChange}
              autoComplete="off"
              className="border border-[#e4dfd8] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
              placeholder="* Kitchen, bath, retrofit, ADU, etc."
            />
            {errors.projectType && (
              <p className="text-xs text-red-600 mt-1">{errors.projectType}</p>
            )}
          </div>

          {/* City and ZIP */}
          <div className="flex flex-col text-left">
            <label
              htmlFor="qq-cityZip"
              className="text-xs uppercase tracking-[2px] text-gray mb-1"
            >
              City And ZIP
            </label>
            <input
              id="qq-cityZip"
              type="text"
              name="cityZip"
              required
              value={form.cityZip}
              onChange={handleChange}
              autoComplete="off"
              className={`border rounded-md px-3 py-2 text-sm focus:outline-none ${
                errors.cityZip ? 'border-red-500' : 'border-[#e4dfd8] focus:border-primary'
              }`}
              placeholder="* Example, Studio City, CA 91604"
            />
            {errors.cityZip && (
              <p className="text-xs text-red-600 mt-1">{errors.cityZip}</p>
            )}
          </div>

            {/* Submit row */}
            <div className="md:col-span-2 flex flex-col md:flex-row items-center gap-3 mt-2">
              <button
                type="submit"
                disabled={!recaptchaReady}
                className="w-full md:w-auto bg-primary text-white px-10 py-3 uppercase text-sm tracking-[3px] font-light hover:bg-black transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Request My Free Estimate
              </button>
              <div className="text-sm md:text-base text-gray text-center md:text-left">
                Or call{' '}
                <a href="tel:13233004130" className="text-primary underline">
                  (323) 300 4130
                </a>{' '}
                to speak with a specialist now.
              </div>
            </div>
          </form>

          <p className="text-[11px] text-gray mt-4 text-center md:text-left opacity-70">
            By submitting this form you agree to being contacted by Modern Build or its contractors, and to be added to the mailing list. We respect your privacy and will never share your information.
          </p>
        </div>
      </div>
    </section>
  );
};

export default QuickQuoteForm;
